/* ==========================================================================
   Quick View & Wishlist — Combined JavaScript
   Perfume Garden Theme
   ========================================================================== */

/* --------------------------------------------------------------------------
   1. Toast Notification Utility
   -------------------------------------------------------------------------- */

const PGToast = {
  _el: null,
  _timeout: null,

  _getEl() {
    if (!this._el) {
      this._el = document.createElement('div');
      this._el.className = 'pg-toast';
      this._el.setAttribute('role', 'status');
      this._el.setAttribute('aria-live', 'polite');
      document.body.appendChild(this._el);
    }
    return this._el;
  },

  show(message, type = 'success', duration = 2500) {
    const el = this._getEl();
    clearTimeout(this._timeout);
    el.textContent = message;
    el.className = 'pg-toast';
    if (type === 'error') el.classList.add('pg-toast--error');

    // Force reflow for animation restart
    void el.offsetWidth;
    el.classList.add('pg-toast--visible');

    this._timeout = setTimeout(() => {
      el.classList.remove('pg-toast--visible');
    }, duration);
  },
};

/* --------------------------------------------------------------------------
   2. Wishlist Manager — Singleton
   -------------------------------------------------------------------------- */

const PGWishlist = {
  STORAGE_KEY: 'pg_wishlist',
  _items: null,
  _isLoggedIn: false,
  _syncDebounce: null,

  init() {
    // Check if customer is logged in (set by Liquid in theme.liquid)
    this._isLoggedIn = !!window.__pgCustomerLoggedIn;

    // Load from localStorage
    this._items = this._loadFromStorage();

    // Merge with server data if available (injected by Liquid)
    if (this._isLoggedIn && window.__pgWishlistServer) {
      this._mergeServerData(window.__pgWishlistServer);
    }

    // Update all UI elements
    this._updateAllButtons();
    this._updateCounts();
  },

  getItems() {
    if (this._items === null) this._items = this._loadFromStorage();
    return [...this._items];
  },

  has(handle) {
    return this.getItems().includes(handle);
  },

  add(handle) {
    const items = this.getItems();
    if (items.includes(handle)) return false;
    items.push(handle);
    this._items = items;
    this._saveToStorage(items);
    this._syncToServer(items);
    this._updateAllButtons();
    this._updateCounts();
    this._publishUpdate();
    return true;
  },

  remove(handle) {
    let items = this.getItems();
    const idx = items.indexOf(handle);
    if (idx === -1) return false;
    items.splice(idx, 1);
    this._items = items;
    this._saveToStorage(items);
    this._syncToServer(items);
    this._updateAllButtons();
    this._updateCounts();
    this._publishUpdate();
    return true;
  },

  toggle(handle) {
    if (this.has(handle)) {
      this.remove(handle);
      return false; // removed
    } else {
      this.add(handle);
      return true; // added
    }
  },

  count() {
    return this.getItems().length;
  },

  _loadFromStorage() {
    try {
      const data = localStorage.getItem(this.STORAGE_KEY);
      if (data) {
        const parsed = JSON.parse(data);
        if (Array.isArray(parsed)) return parsed;
      }
    } catch (e) {
      console.warn('PGWishlist: Failed to read localStorage', e);
    }
    return [];
  },

  _saveToStorage(items) {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(items));
    } catch (e) {
      console.warn('PGWishlist: localStorage full or unavailable', e);
      PGToast.show('Wishlist storage full. Some items may not be saved.', 'error');
    }
  },

  _mergeServerData(serverItems) {
    if (!Array.isArray(serverItems)) return;
    const localItems = this._items || [];
    const merged = [...new Set([...serverItems, ...localItems])];
    if (merged.length !== localItems.length || merged.some((h, i) => h !== localItems[i])) {
      this._items = merged;
      this._saveToStorage(merged);
      this._syncToServer(merged);
    }
  },

  _syncToServer(items) {
    if (!this._isLoggedIn) return;

    // Debounce server sync to avoid hammering the API
    clearTimeout(this._syncDebounce);
    this._syncDebounce = setTimeout(() => {
      const body = JSON.stringify({
        attributes: {
          wishlist: JSON.stringify(items),
        },
      });

      fetch(window.routes.cart_update_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: body,
      }).catch((err) => {
        console.warn('PGWishlist: Failed to sync to server', err);
      });
    }, 500);
  },

  _updateAllButtons() {
    // Guard: prevent re-entrant calls from triggering the MutationObserver again
    if (this._updatingButtons) return;
    this._updatingButtons = true;
    try {
      document.querySelectorAll('.pg-wishlist-btn').forEach((btn) => {
        const handle = btn.dataset.productHandle;
        const isActive = this.has(handle);
        btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        btn.setAttribute(
          'aria-label',
          isActive
            ? `Remove ${btn.dataset.productTitle || ''} from wishlist`
            : `Add ${btn.dataset.productTitle || ''} to wishlist`
        );
        btn.title = isActive ? 'Remove from Wishlist' : 'Add to Wishlist';
      });
    } finally {
      this._updatingButtons = false;
    }
  },

  _updateCounts() {
    const count = this.count();
    // Header count bubble
    document.querySelectorAll('.wishlist-count-bubble').forEach((bubble) => {
      const span = bubble.querySelector('span[aria-hidden]');
      if (span) span.textContent = count;
      bubble.style.display = count > 0 ? '' : 'none';
    });
    // Drawer count
    document.querySelectorAll('.drawer-wishlist-count').forEach((el) => {
      el.textContent = count;
    });
  },

  _publishUpdate() {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.wishlistUpdate, { items: this.getItems(), count: this.count() });
    }
  },
};

/* --------------------------------------------------------------------------
   3. Wishlist Button Click Handler (delegated)
   -------------------------------------------------------------------------- */

document.addEventListener('click', (e) => {
  const btn = e.target.closest('.pg-wishlist-btn');
  if (!btn) return;

  e.preventDefault();
  e.stopPropagation();

  const handle = btn.dataset.productHandle;
  if (!handle) return;

  const added = PGWishlist.toggle(handle);

  // Animate heart
  if (added) {
    btn.classList.add('pg-wishlist-animate');
    setTimeout(() => btn.classList.remove('pg-wishlist-animate'), 450);
    PGToast.show('Added to Wishlist ❤️');
  } else {
    PGToast.show('Removed from Wishlist');
  }
});

/* --------------------------------------------------------------------------
   4. Wishlist Page Renderer
   -------------------------------------------------------------------------- */

class PGWishlistPage {
  constructor(container) {
    this.container = container;
    this.gridEl = container.querySelector('.wishlist-page__grid');
    this.emptyEl = container.querySelector('.wishlist-page__empty');
    this.countEl = container.querySelector('.wishlist-page__count');
    this.loadingEl = container.querySelector('.wishlist-page__loading');

    this.render();
  }

  async render() {
    const handles = PGWishlist.getItems();

    if (handles.length === 0) {
      this._showEmpty();
      return;
    }

    this._showLoading(handles.length);

    const products = await this._fetchProducts(handles);
    this._renderGrid(products, handles);
    this._updateCount(products.length);
  }

  _showEmpty() {
    if (this.gridEl) this.gridEl.style.display = 'none';
    if (this.loadingEl) this.loadingEl.style.display = 'none';
    if (this.emptyEl) this.emptyEl.style.display = '';
    if (this.countEl) this.countEl.textContent = '0 items';
  }

  _showLoading(count) {
    if (this.emptyEl) this.emptyEl.style.display = 'none';
    if (this.gridEl) {
      this.gridEl.style.display = '';
      this.gridEl.innerHTML = Array(count)
        .fill(0)
        .map(
          () => `
        <li class="wishlist-card-wrapper wishlist-card-wrapper--skeleton" style="position: relative; aspect-ratio: 1/1; background-color: rgba(var(--color-foreground), 0.04); border-radius: var(--card-corner-radius);">
        </li>
      `
        )
        .join('');
    }
  }

  async _fetchProducts(handles) {
    const rootUrl = window.Shopify?.routes?.root || '/';
    const fetches = handles.map(async (handle) => {
      try {
        const resp = await fetch(`${rootUrl}products/${handle}.js`);
        if (resp.ok) {
          const product = await resp.json();
          product._handle = handle;
          return product;
        }
        return { _handle: handle, _unavailable: true };
      } catch (err) {
        console.warn(`PGWishlistPage: Failed to fetch ${handle}`, err);
        return { _handle: handle, _unavailable: true };
      }
    });
    return Promise.all(fetches);
  }

  _renderGrid(products, originalHandles) {
    if (!this.gridEl) return;

    if (products.length === 0) {
      this._showEmpty();
      return;
    }

    this.gridEl.innerHTML = products
      .map((product) => {
        if (product._unavailable) {
          return this._renderUnavailableCard(product);
        }
        return this._renderProductCard(product);
      })
      .join('');

    // Bind events for remove buttons
    this.gridEl.querySelectorAll('.wishlist-card__remove').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const handle = btn.dataset.handle;
        PGWishlist.remove(handle);
        PGToast.show('Removed from Wishlist');
        // Re-render
        this.render();
      });
    });

    // Bind events for direct Add to Cart buttons
    this.gridEl.querySelectorAll('.wishlist-add-to-cart-btn').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this._addToCart(btn);
      });
    });
  }

  _renderProductCard(product) {
    const handle = product.handle || product._handle;
    const title = product.title || handle;
    const vendor = product.vendor || 'EVERGREEN COLLECTION';
    const url = product.url || `/products/${handle}`;
    const variants = product.variants || [];
    const firstVariant = variants[0] || {};
    const hasMultipleVariants = variants.length > 1;
    const isAvailable = !!product.available;
    const price = product.price || 0;
    const comparePrice = product.compare_at_price || 0;
    const onSale = comparePrice > price;

    // Image resolution
    const images = product.images || [];
    const featuredImg = product.featured_image || images[0] || '';
    const secondaryImg = images.length > 1 ? images[1] : null;
    const imgSrc = this._imgUrl(featuredImg, 600);
    const imgSrc2 = secondaryImg ? this._imgUrl(secondaryImg, 600) : null;

    // Badge
    let badgeHtml = '';
    if (!isAvailable) {
      badgeHtml = `
        <div class="bestseller-card__badge" style="background-color: #6b7280; color: #FFFFFF;">
          SOLD OUT
        </div>
      `;
    } else if (onSale) {
      badgeHtml = `
        <div class="bestseller-card__badge" style="background-color: #FF3366; color: #FFFFFF;">
          SALE
        </div>
      `;
    }

    // Action button markup
    let actionBtnHtml = '';
    if (!isAvailable) {
      actionBtnHtml = `
        <button
          type="button"
          class="bestseller-card__button bestseller-card__button--sold-out"
          disabled
          aria-disabled="true"
          style="background-color: #f3f4f6; color: #9ca3af; cursor: not-allowed;"
        >
          <span>SOLD OUT</span>
        </button>
      `;
    } else if (hasMultipleVariants) {
      actionBtnHtml = `
        <modal-opener data-modal="#QuickAdd-Modal">
          <button
            type="button"
            class="bestseller-card__button pg-quick-view-trigger"
            aria-haspopup="dialog"
            aria-label="Choose options for ${this._escape(title)}"
            data-product-url="${url}"
            data-product-handle="${this._escape(handle)}"
            style="background-color: #EA204A; color: #FFFFFF;"
          >
            <span>CHOOSE OPTIONS</span>
            <div class="loading__spinner hidden">
              <svg class="spinner" viewBox="0 0 66 66"><circle stroke-width="6" cx="33" cy="33" r="30" fill="none" class="path"/></svg>
            </div>
          </button>
        </modal-opener>
      `;
    } else {
      actionBtnHtml = `
        <button
          type="button"
          class="bestseller-card__button wishlist-add-to-cart-btn"
          data-variant-id="${firstVariant.id}"
          data-product-handle="${this._escape(handle)}"
          aria-label="Add ${this._escape(title)} to cart"
          style="background-color: #EA204A; color: #FFFFFF;"
        >
          <span>ADD TO CART</span>
          <div class="loading__spinner hidden">
            <svg class="spinner" viewBox="0 0 66 66"><circle stroke-width="6" cx="33" cy="33" r="30" fill="none" class="path"/></svg>
          </div>
        </button>
      `;
    }

    return `
      <li style="list-style: none;">
        <div class="bestseller-card wishlist-card-wrapper" data-handle="${this._escape(handle)}">
          <div class="bestseller-card__image-wrapper">
            ${badgeHtml}

            <a href="${url}" tabindex="-1" class="bestseller-card__image-link${imgSrc2 ? ' has-secondary-image' : ''}">
              ${imgSrc ? `
                <img
                  src="${imgSrc}"
                  alt="${this._escape(title)}"
                  class="bestseller-card__image bestseller-card__image--primary"
                  loading="lazy"
                  width="600"
                  height="600"
                >
                ${imgSrc2 ? `
                  <img
                    src="${imgSrc2}"
                    alt="${this._escape(title)}"
                    class="bestseller-card__image bestseller-card__image--secondary"
                    loading="lazy"
                    width="600"
                    height="600"
                  >
                ` : ''}
              ` : `
                <div class="placeholder-svg" style="width: 100%; height: 100%; background: #F6F4ED;"></div>
              `}
            </a>

            <div class="pg-card-icons" style="z-index: 2;">
              <button
                type="button"
                class="pg-card-icon-btn wishlist-card__remove"
                data-handle="${this._escape(handle)}"
                aria-label="Remove ${this._escape(title)} from wishlist"
                title="Remove from Wishlist"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>

              <div class="pg-quick-view-btn">
                <modal-opener data-modal="#QuickAdd-Modal">
                  <button
                    type="button"
                    class="pg-card-icon-btn pg-quick-view-trigger"
                    aria-label="Quick view ${this._escape(title)}"
                    data-product-url="${url}"
                    data-product-handle="${this._escape(handle)}"
                    title="Quick View"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                    <div class="loading__spinner hidden">
                      <svg xmlns="http://www.w3.org/2000/svg" class="spinner" viewBox="0 0 66 66"><circle stroke-width="6" cx="33" cy="33" r="30" fill="none" class="path"/></svg>
                    </div>
                  </button>
                </modal-opener>
              </div>
            </div>
          </div>

          <div class="bestseller-card__details">
        

            <a href="${url}" class="bestseller-card__title">
              ${this._escape(title)}
            </a>

            <div class="bestseller__stars">
              <span class="bestseller__stars-icons">
                <svg viewBox="0 0 12 12"><path d="M6 0L7.80568 3.86877L12 4.41724L8.91866 7.31123L9.7082 11.4828L6 9.42055L2.2918 11.4828L3.08134 7.31123L0 4.41724L4.19432 3.86877L6 0Z"/></svg>
                <svg viewBox="0 0 12 12"><path d="M6 0L7.80568 3.86877L12 4.41724L8.91866 7.31123L9.7082 11.4828L6 9.42055L2.2918 11.4828L3.08134 7.31123L0 4.41724L4.19432 3.86877L6 0Z"/></svg>
                <svg viewBox="0 0 12 12"><path d="M6 0L7.80568 3.86877L12 4.41724L8.91866 7.31123L9.7082 11.4828L6 9.42055L2.2918 11.4828L3.08134 7.31123L0 4.41724L4.19432 3.86877L6 0Z"/></svg>
                <svg viewBox="0 0 12 12"><path d="M6 0L7.80568 3.86877L12 4.41724L8.91866 7.31123L9.7082 11.4828L6 9.42055L2.2918 11.4828L3.08134 7.31123L0 4.41724L4.19432 3.86877L6 0Z"/></svg>
                <svg viewBox="0 0 12 12"><path d="M6 0L7.80568 3.86877L12 4.41724L8.91866 7.31123L9.7082 11.4828L6 9.42055L2.2918 11.4828L3.08134 7.31123L0 4.41724L4.19432 3.86877L6 0Z"/></svg>
              </span>
              <span>(7)</span>
            </div>

            <div class="bestseller-card__price-wrapper">
              <span class="bestseller-card__price-current">${this._formatMoney(price)}</span>
              ${onSale ? `<span class="bestseller-card__price-compare">${this._formatMoney(comparePrice)}</span>` : ''}
            </div>

            <div class="bestseller-card__form">
              ${actionBtnHtml}
            </div>
          </div>
        </div>
      </li>
    `;
  }

  _renderUnavailableCard(product) {
    return `
      <li class="wishlist-card-wrapper wishlist-card-wrapper--unavailable" style="display: flex; flex-direction: column; justify-content: center; align-items: center; background: rgba(var(--color-foreground), 0.04); border-radius: var(--card-corner-radius); padding: 2rem; text-align: center;">
        <p>This product is no longer available.</p>
        <button class="button button--secondary wishlist-card__remove" data-handle="${product._handle}" style="position: static; transform: none; width: auto; height: auto; border-radius: var(--buttons-radius); margin-top: 1rem; padding: 0.8rem 1.5rem;">Remove</button>
      </li>
    `;
  }

  async _addToCart(btn) {
    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    btn.classList.add('loading');
    btn.disabled = true;
    const spinner = btn.querySelector('.loading__spinner');
    if (spinner) spinner.classList.remove('hidden');

    try {
      const config = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [{ id: parseInt(variantId), quantity: 1 }],
        }),
      };

      const resp = await fetch(window.routes.cart_add_url + '.js', config);
      const data = await resp.json();

      if (data.status) {
        // Error from Shopify
        PGToast.show(data.description || 'Failed to add to cart', 'error');
      } else {
        PGToast.show('Added to Cart! 🛒');
        // Update cart drawer/notification
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'wishlist-page', cartData: data });
        }
        // Refresh cart UI
        const cartDrawer = document.querySelector('cart-drawer');
        const cartNotification = document.querySelector('cart-notification');
        if (cartDrawer || cartNotification) {
          // Fetch fresh sections
          const sectionsUrl = window.location.pathname;
          const cart = cartDrawer || cartNotification;
          const sections = cart.getSectionsToRender().map((s) => s.id);
          const sectionsResp = await fetch(
            `${window.routes.cart_url}?sections=${sections.join(',')}`
          );
          const sectionsData = await sectionsResp.json();
          cart.renderContents({ sections: sectionsData });
        }
      }
    } catch (err) {
      console.error('PGWishlistPage: Add to cart failed', err);
      PGToast.show('Something went wrong. Please try again.', 'error');
    } finally {
      btn.classList.remove('loading');
      btn.disabled = false;
      const spinner = btn.querySelector('.loading__spinner');
      if (spinner) spinner.classList.add('hidden');
    }
  }

  _formatMoney(cents) {
    if (typeof cents !== 'number') cents = 0;
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, window.theme?.moneyFormat || 'Rs. {{amount}}');
    }
    const formatted = (cents / 100).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return `Rs. ${formatted}`;
  }

  _imgUrl(src, width = 533) {
    if (!src) return '';
    const clean = (typeof src === 'string' ? src : src.src || '').replace(/^\/\//, 'https://');
    if (!clean) return '';
    const sep = clean.includes('?') ? '&' : '?';
    return `${clean}${sep}width=${width}`;
  }

  _updateCount(count) {
    if (this.countEl) {
      this.countEl.textContent = `${count} item${count !== 1 ? 's' : ''}`;
    }
  }

  _escape(str) {
    if (!str) return '';
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
}

/* --------------------------------------------------------------------------
   5. Initialize on DOM Ready
   -------------------------------------------------------------------------- */

function pgInitQuickViewWishlist() {
  // Initialize wishlist
  PGWishlist.init();

  // Initialize wishlist page if present
  const wishlistPageContainer = document.querySelector('.wishlist-page');
  if (wishlistPageContainer) {
    new PGWishlistPage(wishlistPageContainer);
  }

  // Listen for wishlist updates to re-sync buttons (e.g. after AJAX page loads)
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.wishlistUpdate, () => {
      PGWishlist._updateAllButtons();
      PGWishlist._updateCounts();
    });
  }

  // Update wishlist buttons after cart changes (covers cart drawer re-render)
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      // Small delay to allow the cart DOM to settle before updating buttons
      setTimeout(() => {
        PGWishlist._updateAllButtons();
        PGWishlist._updateCounts();
      }, 150);
    });
  }

  // Update wishlist buttons after Quick Add modal opens
  document.addEventListener('modalOpened', () => {
    setTimeout(() => {
      PGWishlist._updateAllButtons();
      PGWishlist._updateCounts();
    }, 100);
  });
}

// Run init when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', pgInitQuickViewWishlist);
} else {
  pgInitQuickViewWishlist();
}
